import type { HelpEntry } from './help-types';

export interface HelpVisibility {
  experimentalFeaturesEnabled: boolean;
  isAdmin: boolean;
}

/** Entries the viewer may see: gated entries need their flag, admin entries need an admin. */
export function visibleHelpEntries(entries: readonly HelpEntry[], visibility: HelpVisibility): HelpEntry[] {
  return entries.filter((entry) => {
    if (entry.gatedBy === 'experimentalFeatures' && !visibility.experimentalFeaturesEnabled) return false;
    if (entry.audience === 'admin' && !visibility.isAdmin) return false;
    return true;
  });
}

/** Literal route segments a `:param` never stands for (`/clients/new` is not client "new"). */
const RESERVED_SEGMENTS = new Set(['new']);

function segments(path: string): string[] {
  return path.replace(/[?#].*$/, '').split('/').filter(Boolean);
}

/**
 * How well `pattern` (`/clients/:id`) matches `pathname`: null when it does not match,
 * otherwise a score where more static segments rank higher (most specific first).
 */
export function routeMatchScore(pattern: string, pathname: string): number | null {
  const want = segments(pattern);
  const have = segments(pathname);
  if (want.length !== have.length) return null;
  let score = 0;
  for (let i = 0; i < want.length; i += 1) {
    if (want[i].startsWith(':')) {
      if (RESERVED_SEGMENTS.has(have[i])) return null;
      score += 1;
    } else if (want[i] === have[i]) {
      score += 10;
    } else {
      return null;
    }
  }
  return score;
}

function bestScore(entry: HelpEntry, pathname: string): number | null {
  let best: number | null = null;
  for (const pattern of [...entry.routes, ...entry.alsoShownIn]) {
    const score = routeMatchScore(pattern, pathname);
    if (score !== null && (best === null || score > best)) best = score;
  }
  return best;
}

/** Entries for the screen at `pathname`, most specific route match first, then registry order. */
export function helpEntriesForPath(entries: readonly HelpEntry[], pathname: string): HelpEntry[] {
  return entries
    .map((entry, order) => ({ entry, order, score: bestScore(entry, pathname) }))
    .filter((item): item is { entry: HelpEntry; order: number; score: number } => item.score !== null)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .map((item) => item.entry);
}
